import * as React from 'react';
import Button from 'src/components/Button';
import DatePicker from 'src/components/DatePicker';
import Input from 'src/components/FormElements/Input';
import Select from 'src/components/FormElements/Select';
import Icon from 'src/components/Icon';
import { Column, Columns } from 'src/components/Layout/Grid';
import { formatDate } from 'src/utils/dateUtils';
import FieldError from 'src/components/FormElements/FieldError';
import { addToast, clearToasts } from 'src/components/Toast';
import ContentEditor from 'src/components/ContentEditor';
import { Editor } from 'tinymce';

export interface IAdminNewsFormValues {
  newsId?: number;
  newsDate?: Date | null;
  newsDateExpire?: Date | null;
  title?: string;
  newsLabel?: string;
  content?: string;
}

export interface IAdminNewsFormSubmitValues {
  newsDate: string | null;
  newsDateExpire: string | null;
  title: string;
  newsLabel: string | null;
  content: string;
}

export interface IAdminNewsFormProps {
  values?: IAdminNewsFormValues;
  onSubmit(values: IAdminNewsFormSubmitValues): void;
  onCancel(): void;
  onDelete?(): void;
}

interface IAdminNewsFormErrors {
  [key: string]: string;
}

const defaultValues = {
  newsDate: null,
  newsDateExpire: null,
  title: '',
  newsLabel: '',
  content: ''
};

const validate = (values: IAdminNewsFormValues): (null | IAdminNewsFormErrors) =>  {
  const errors: IAdminNewsFormErrors = {};
  let valid = true;

  if (!values.newsDate) {
    valid = false;
    errors['newsDate'] = 'skal udfyldes';
  }

  if (values.title !== undefined && values.title.length > 70) {
    valid = false;
    errors['title'] = 'må ikke være længere end 70 tegn';
  } else if (!values.title || values.title.trim().length === 0) {
    valid = false;
    errors['title'] = 'skal udfyldes';
  }

  return valid ? null : errors;
}

const AdminNewsForm = (props: IAdminNewsFormProps) => {
  const [values, setValues] = React.useState<IAdminNewsFormValues>({
    ...defaultValues
  });
  const editorRef = React.useRef<Editor | null>(null);
  const [errors, setErrors] = React.useState<IAdminNewsFormErrors>({});
  const isNewItem = React.useMemo(
    () => (values.newsId ? false : true),
    [values]
  );

  React.useEffect(() => {
    const overrideValues = props.values || {};
    setValues({ ...defaultValues, ...overrideValues });
    setErrors({});
  }, [props.values]);

  React.useEffect(() => {
    editorRef.current?.setContent(values.content || '');
  }, [values.content]);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { target } = event;
    setValues({ ...values, [target.name]: target.value });
  };
  const handleNewsDateChange = (date: Date) => {
    setValues({ ...values, newsDate: date });
  };

  const handleNewsDateExpireChange = (date: Date) => {
    setValues({ ...values, newsDateExpire: date });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    clearToasts();

    const validationErrors = validate(values);
    if (validationErrors !== null) {
      setErrors(validationErrors);
      addToast({ type: 'error', message: 'Formularen er ikke udfyldt korrekt' });
      return;
    }

    const submitValues = {
      title: values.title ? values.title.trim() : '',
      newsLabel: values.newsLabel || null,
      content: editorRef.current?.getContent() || '',
      newsDate: values.newsDate
        ? formatDate(new Date(values.newsDate), 'yyyy-MM-dd')
        : null,
      newsDateExpire: values.newsDateExpire
        ? formatDate(new Date(values.newsDateExpire), 'yyyy-MM-dd')
        : null
    };

    setErrors({});
    props.onSubmit(submitValues);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Columns gutter="large">
        <Column lg={4} className="mb-6">
          <label
            htmlFor="newsDate"
            className="font-bold f5 uppercase block mb-1"
          >
            Dato for nyhed
          </label>
          <DatePicker
            onChange={handleNewsDateChange}
            dateFormat="d. MMMM yyyy"
            name="newsDate"
            id="newsDate"
            autoComplete="off"
            placeholderText="Vælg dato"
            selected={values.newsDate}
            customInput={(
              <Input
                variant="inset"
                elementSize="large"
                showErrorBorder={errors['newsDate'] !== undefined}
              />
            )}
          />
          {errors['newsDate'] && (
            <FieldError className="mt-1">{errors['newsDate']}</FieldError>
          )}
        </Column>
        <Column lg={4} className="mb-6">
          <label
            htmlFor="newsDateExpire"
            className="font-bold f5 uppercase block mb-1"
          >
            Evt. udløbsdato
          </label>
          <DatePicker
            onChange={handleNewsDateExpireChange}
            dateFormat="d. MMMM yyyy"
            name="newsDateExpire"
            id="newsDateExpire"
            placeholderText="Vælg dato"
            autoComplete="off"
            selected={values.newsDateExpire}
            customInput={<Input variant="inset" elementSize="large" />}
          />
        </Column>
        <Column lg={4} className="mb-6">
          <label
            htmlFor="newsLabel"
            className="font-bold f5 uppercase block mb-1"
          >
            Evt. Label
          </label>
          <Select
            name="newsLabel"
            id="newsLabel"
            className="w-full block"
            onChange={handleInputChange}
            value={values.newsLabel}
            variant="light"
            elementSize="large"
          >
            <option value="">Vælg label</option>
            <option value="new">Nyt</option>
            <option value="cancelled">Aflyst</option>
          </Select>
        </Column>
      </Columns>
      <div className="mb-6">
        <label htmlFor="title" className="font-bold f5 uppercase block mb-1">
          Overskrift (max 70 tegn)
        </label>
        <Input
          type="text"
          name="title"
          id="title"
          variant="inset"
          elementSize="large"
          className="w-full block"
          autoComplete="off"
          value={values.title}
          onChange={handleInputChange}
          maxLength={70}
          showErrorBorder={errors['title'] !== undefined}
        />
        <div className="flex items-center">
          {errors['title'] && (
            <FieldError className="mt-1">{errors['title']}</FieldError>
          )}

          <div className="f6 has-text-gray-700 mt-1 font-bold ml-auto">
            {`Tegn brugt: ${values.title?.length || 0}/70`}
          </div>
        </div>
      </div>
      <div className="mb-6">
        <label htmlFor="content" className="font-bold f5 uppercase block mb-1">
          Indhold
        </label>
        <ContentEditor
          ref={editorRef}
          initialValue={values.content || ''}
        />
      </div>

      <div className="flex justify-end mt-6">
        {props.onDelete !== undefined && (
          <Button
            type="button"
            onClick={props.onDelete}
            color="danger"
            className="mr-auto"
          >
            Slet nyhed
            <Icon name="cross" className="ml-2" />
          </Button>
        )}
        <Button
          type="button"
          onClick={props.onCancel}
          color="gray"
          className="ml-2"
        >
          Fortryd
        </Button>
        <Button
          type="submit"
          className="ml-2"
          color={isNewItem ? 'success' : 'primary'}
        >
          {isNewItem ? 'Opret' : 'Opdater'}
          {isNewItem ? (
            <Icon name="chevron-right" className="ml-2" />
          ) : (
            <Icon name="refresh" className="ml-2" />
          )}
        </Button>
      </div>
    </form>
  );
};

export default AdminNewsForm;



// WEBPACK FOOTER //
// ./src/Pages/CoursePortal/AdminNews/components/NewsForm.tsx